import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
//import { useNavigation } from '@react-navigation/native';
// Layouts
import MainLayout from './layouts/MainLayout';

// Pages
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import EnterpriseOnboardingPage from './pages/EnterpriseOnboardingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PricingPage from './pages/PricingPage';
import SubscriptionPage from './pages/SubscriptionPage';
import DashboardPage from './pages/DashboardPage';
import ResearchPage from './pages/ResearchPage';
import OutlookReviewsPage from './pages/OutlookReviewsPage';
import RolesPage from './pages/RolesPage';
import MatchingPage from './pages/MatchingPage';
import NetworkPage from './pages/NetworkPage';
import AIAssistantPage from './pages/AIAssistantPage';
import TeamPage from './pages/TeamPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
//import { onboardingService } from './services/onboardingService';
import useOnboardingProgress from "@/hooks/useOnboardingProgress";
import { startTokenRefreshWatcher } from './services/tokenManager';
export default function App() {
  useEffect(() => startTokenRefreshWatcher(), []);

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const [selectedRole, setSelectedRole] = useState('investor');
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userPlan, setUserPlan] = useState(localStorage.getItem('plan') || 'INDIVIDUAL');
  const [enterpriseOrgSetupDone, setEnterpriseOrgSetupDone] = useState(localStorage.getItem('enterpriseOrgSetupDone') === 'true');
  const { saveProgress } = useOnboardingProgress();
  const [isResetOnboarding, setIsResetOnboarding] = useState(false);

  // Check authentication and onboarding status on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const onboardingStatus = localStorage.getItem('onboardingComplete');
    const savedRole = localStorage.getItem('userRole');
    const token = localStorage.getItem('authToken');
  
    console.log('Auth check:', { authStatus, onboardingStatus, savedRole, token });
    if (authStatus === 'true' && token) {
      setIsAuthenticated(true);
      const savedPlan = localStorage.getItem('plan');
      if (savedPlan) setUserPlan(savedPlan);
      const esod = localStorage.getItem('enterpriseOrgSetupDone');
      setEnterpriseOrgSetupDone(esod === 'true');
      
      if (onboardingStatus === 'true' && savedRole) {
        setOnboardingComplete(true);
        setSelectedRole(savedRole);
      }
    }

    // if (isResetOnboarding) {
    //   console.log('Navigating to /onboarding from handleResetOnboarding');
    //   navigate('/onboarding');
    // }
    
    setIsLoading(false);
  }, []);

  const handleLogin = (email, isNewUser) => {
    const savedPlan = localStorage.getItem('plan');
    if (savedPlan) setUserPlan(savedPlan);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userEmail', email);
    setIsAuthenticated(true);
    
    // If new user, they'll need to complete onboarding
    // If existing user, check if they've completed onboarding
    if (isNewUser) {
      setOnboardingComplete(false);
    } else {
      const onboardingStatus = localStorage.getItem('onboardingComplete') || false;
      const savedRole = localStorage.getItem('userRole');
      
      if (onboardingStatus === 'true' && savedRole) {
        setOnboardingComplete(true);
        setSelectedRole(savedRole);
      }
    }
    console.log('Auth check:', { isAuthenticated, onboardingComplete, email, plan: savedPlan });
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('onboardingComplete');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
    localStorage.removeItem('plan');
    localStorage.removeItem('orgAccessRequired');
    localStorage.removeItem('orgId');
    localStorage.removeItem('orgName');
    localStorage.removeItem('membershipStatus');
    localStorage.removeItem('subscriptionStatus');
        localStorage.removeItem('trialStart');
        localStorage.removeItem('trialEnd');
    localStorage.removeItem('daysRemaining');
    localStorage.removeItem('aiQuotaRemaining');
    localStorage.removeItem('aiQuotaTotal');
    setIsAuthenticated(false);
    setOnboardingComplete(false);
  };

  const needsSubscription = () => {
    const role = localStorage.getItem('userRole');
    // Self-directed investors never require a subscription.
    if (role === 'INVESTOR') return false;
    const subRequired = localStorage.getItem('subscriptionRequired');
    if (subRequired === 'false') {
      // Non-owner org member — check if org subscription is usable
      const status = localStorage.getItem('subscriptionStatus');
      if (status === 'ACTIVE' || status === 'COVERED_BY_ORG' || status === 'TRIALING') return false;
      if (status === 'CANCELED') {
        const daysLeft = parseInt(localStorage.getItem('daysRemaining') || '0', 10);
        if (daysLeft > 0) return false;
      }
      return !!localStorage.getItem('orgOwnerEmail');
    }
    const status = localStorage.getItem('subscriptionStatus');
    if (status === 'ACTIVE' || status === 'COVERED_BY_ORG') return false;
    if (status === 'TRIALING' && localStorage.getItem('trialSubscriptionSkipped') === 'true') return false;
    const plan = localStorage.getItem('plan');
    if (role === 'ADVISOR' || role === 'ANALYST') return true;
    if (plan === 'ENTERPRISE') return true;
    return false;
  };

  const handleOnboardingComplete = (data, role) => {
    if (role) {
      localStorage.setItem('onboardingComplete', 'true');
      localStorage.removeItem('enterpriseOrgSetupDone');
      localStorage.setItem('userRole', role);
      localStorage.setItem('userData', JSON.stringify(data));
      setSelectedRole(role);
      setOnboardingComplete(true);
      setEnterpriseOrgSetupDone(false);
      const plan = localStorage.getItem('plan');
      if (needsSubscription()) {
        navigate('/subscription');
      } else {
        navigate(plan === 'ENTERPRISE' ? '/admin' : '/dashboard');
      }
    }
  };

  const handleOrgSetupComplete = () => {
    setEnterpriseOrgSetupDone(true);
  };

  const handleResetOnboarding = () => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.error('No user ID found');
      return;
    }

    // Update local state first for immediate UI feedback
    //setOnboardingComplete(false);
    //setSelectedRole(null);

    // Update local storage
    // localStorage.setItem('userRole', null);
    // localStorage.setItem('onboardingComplete', 'false');
    // localStorage.setItem('onboardingStep', '0');
    // localStorage.setItem('currentStep', '0');
    // localStorage.setItem('isAuthenticated', 'true'); // Ensure auth state is maintained

    const userData = JSON.parse(localStorage.getItem('userData'));
    userData.currentStep = 0;
    userData.completed = false;
    localStorage.setItem('userData', JSON.stringify(userData));
    console.log('userData', userData);
    localStorage.setItem('isResetOnboarding', 'true');
    console.log('setting isResetOnboarding', localStorage.getItem('isResetOnboarding'));
    //await new Promise(resolve => setTimeout(resolve, 50));
    // Update backend
    try {
      saveProgress(userId, userData);
    } catch (error) {
      console.error('Error saving progress:', error);
      // Continue with navigation even if save fails
    }
    localStorage.removeItem('onboardingComplete');
    localStorage.setItem('onboardingStep', 0);
    localStorage.setItem('currentStep', 0);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
    setOnboardingComplete(false);
    new Promise(resolve => setTimeout(resolve, 100));
    //setIsResetOnboarding(true);
    //localStorage.setItem('isResetOnboarding', 'true');
    // Instead of window.location
    //window.location.href = '/onboarding?reset=' + Date.now();
    //window.location.reload('/onboarding');
  } catch (error) {
    console.error('Error in handleResetOnboarding:', error);
    // Fallback to full page reload if navigation fails
    //window.location.href = '/onboarding?reset=' + Date.now();
    //window.location.reload();
  }
};

  const handleResetOnboardingOld = () => {
    const userData = JSON.parse(localStorage.getItem('userData'));
    const userId = localStorage.getItem('userId');
    userData.currentStep = 0;
    userData.completed = false;
    const response = saveProgress(userId, userData);
    console.log(response);
    localStorage.setItem('onboardingComplete', 'false');
    localStorage.setItem('onboardingStep', 0);
    //localStorage.removeItem('userRole');
    //localStorage.removeItem('userData');
    setOnboardingComplete(false);
    setSelectedRole(null);
    //window.location.href = '/onboarding';
    navigate('/onboarding');
    //return;
  };

  const isSubscriptionExpired = () => {
    const role = localStorage.getItem('userRole');
    // Self-directed investors never require a subscription.
    if (role === 'INVESTOR') return false;
    const status = localStorage.getItem('subscriptionStatus');
    if (status === 'COVERED_BY_ORG' || status === 'ACTIVE' || status === 'TRIALING') return false;
    const subRequired = localStorage.getItem('subscriptionRequired');
    if (subRequired === 'false') {
      // Non-owner org member — check if they need attention
      if (!localStorage.getItem('orgOwnerEmail')) return false;
      if (status === 'CANCELED') {
        const daysLeft = parseInt(localStorage.getItem('daysRemaining') || '0', 10);
        if (daysLeft > 0) return false;
      }
      return status === 'EXPIRED';
    }
    const plan = localStorage.getItem('plan');
    return status === 'EXPIRED' && plan !== 'INVESTOR';
  };

  const needsPlanSubscription = () => {
    const role = localStorage.getItem('userRole');
    // Self-directed investors never require a subscription.
    if (role === 'INVESTOR') return false;
    const status = localStorage.getItem('subscriptionStatus');
    if (status === 'ACTIVE' || status === 'COVERED_BY_ORG' || status === 'TRIALING') return false;
    const subRequired = localStorage.getItem('subscriptionRequired');
    if (subRequired === 'false') {
      // Non-owner org member — check if they need attention
      if (!localStorage.getItem('orgOwnerEmail')) return false;
      if (status === 'CANCELED') {
        const daysLeft = parseInt(localStorage.getItem('daysRemaining') || '0', 10);
        if (daysLeft > 0) return false;
      }
      return true;
    }
    if ((status === 'TRIALING' || status === 'CANCELED') && localStorage.getItem('trialSubscriptionSkipped') === 'true') return false;
    const plan = localStorage.getItem('plan');
    if (role === 'ADVISOR' || role === 'ANALYST') return true;
    if (plan === 'ENTERPRISE') return true;
    return false;
  };

  // Show loading state
  if (isLoading) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <Toaster />
      </>
    );
  }

  return (
    <>
      <Routes>
        {/* Public Routes */}
        {!isAuthenticated ? (
          <>
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : !onboardingComplete ? (
          <>
            {localStorage.getItem('orgAccessRequired') === 'true' && localStorage.getItem('membershipStatus') !== 'ACTIVE' ? (
              <>
                <Route path="/*" element={<EnterpriseOnboardingPage onComplete={handleOnboardingComplete} onLogout={handleLogout} />} />
              </>
            ) : userPlan === 'ENTERPRISE' && !enterpriseOrgSetupDone ? (
              <>
                <Route path="/enterprise-onboarding" element={<EnterpriseOnboardingPage onComplete={handleOnboardingComplete} onLogout={handleLogout} />} />
                <Route path="*" element={<Navigate to="/enterprise-onboarding" replace />} />
              </>
            ) : (
              <>
                <Route path="/onboarding" element={<OnboardingPage onComplete={handleOnboardingComplete} onLogout={handleLogout} />} />
                <Route path="*" element={<Navigate to="/onboarding" replace />} />
              </>
            )}
          </>
        ) : isSubscriptionExpired() ? (
          <Route path="/*" element={<SubscriptionPage onLogout={handleLogout} />} />
        ) : needsPlanSubscription() ? (
          <>
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/subscription" element={<SubscriptionPage onLogout={handleLogout} />} />
            <Route path="*" element={<Navigate to="/subscription" replace />} />
          </>
        ) : (
          /* Protected Routes */
          <>
            <Route
              element={
                <MainLayout
                  selectedRole={selectedRole}
                  setSelectedRole={setSelectedRole}
                  onLogout={handleLogout}
                  onResetOnboarding={handleResetOnboarding}
                />
              }
            >
              <Route path="/dashboard" element={<DashboardPage role={selectedRole} />} />
              <Route path="/research" element={<ResearchPage role={selectedRole} />} />
              <Route path="/outlook-reviews" element={<OutlookReviewsPage role={selectedRole} />} />
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/matching" element={<MatchingPage />} />
              <Route path="/network" element={<NetworkPage />} />
              <Route path="/ai-assistant" element={<AIAssistantPage role={selectedRole} />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/profile" element={<ProfilePage role={selectedRole} />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/subscription" element={<SubscriptionPage onLogout={handleLogout} />} />
              <Route path="/" element={<Navigate to={userPlan === 'ENTERPRISE' ? "/admin" : "/dashboard"} replace />} />
            </Route>
            <Route path="*" element={<Navigate to={userPlan === 'ENTERPRISE' ? "/admin" : "/dashboard"} replace />} />
          </>
        )}
      </Routes>
      <Toaster />
    </>
  );
}